<template>
  <article>
    <ul class="mb-4">
      <SmoothReflow class="overflow-hidden transition-all duration-500 ease-in-out">
        <li
          v-for="(audioWork, index) in visibleAudioWorks"
          :key="index"
          class="text-sm md:text-base flex items-center sm:grid grid-cols-5"
          :class="{ 'border-b border-app-primary-grey--concrete pb-4 mb-4': index + 1 < annotatedAudioWorks.length }"
        >
          <div class="flex-auto col-span-4">
            <h4 v-if="audioWork.composers && audioWork.composers.length" class="mb-1">{{ audioWork.composers.map((composer: CmsData) => composer.title).join(' / ') }}</h4>
            <p v-if="audioWork.parent && audioWork.parent.id" class="text-sm md:text-base font-semibold">
              {{ t_('part-title', { part: audioWork.title, work: audioWork.parent.title }) }} {{ audioWork.extraInfo }}
              <template v-if="audioWork.extraLines.length > 0">({{ audioWork.extraLines.join(', ') }})</template>
            </p>
            <p v-else class="text-sm md:text-base font-semibold">
              {{ audioWork.title }}
              <template v-if="audioWork.extraLines.length > 0">({{ audioWork.extraLines.join(', ') }})</template>
            </p>
          </div>
          <div v-if="audioWork.audio" class="flex pl-2 justify-end">
            <AtomIcon
              :icon="playerStates[audioWork.id] ? 'app-pause' : 'app-play'"
              classes="hover:text-app-primary-1-orange--bittersweet cursor-pointer transform transition duration-200 w-6 shrink-0"
              @click="toggleAudio(audioWork.id)"
            />
          </div>
        </li>
      </SmoothReflow>
    </ul>
    <button v-if="annotatedAudioWorks.length > 4" class="underline inline-block" @click="toggleShowAll">
      {{ showAll ? t_('show-less') : t_('show-more') }}
      <AtomIconChevronDown v-if="annotatedAudioWorks.length > 4" class="ml-2 transform transition duration-200 inline-block w-4 h-4" :class="{ 'rotate-180': showAll }" />
    </button>
    <div>
      <template v-for="audioWork in annotatedAudioWorks">
        <audio v-if="audioWork.audio" :id="audioWork.id" :key="audioWork.id" hidden>
          <source :src="audioWork.audio.url" :type="audioWork.audio.mimeType" />
        </audio>
      </template>
    </div>
  </article>
</template>

<script setup lang="ts">
const props = defineProps({
  audioWorks: { type: Array as PropType<any[]>, required: true },
  headerAudioIsPlaying: { type: Boolean, required: true },
  toggleHeaderAudio: { type: Function, required: true },
})

const audioPlayerStore = useAudioPlayerStore()
const gtmCdpAction = setupGtmCdpAction()
const { t_ } = setupTranslationPrefixer('AudioWorks')
const showAll = ref(false)
const annotatedAudioWorks = computed(() =>
  props.audioWorks.map((audioWork) => ({
    ...audioWork,
    extraLines: [audioWork.extraInfo, audioWork.encore ? t_('has-encore') : false].filter((value) => !!value),
  })),
)
const visibleAudioWorks = computed(() => (showAll.value ? annotatedAudioWorks.value : annotatedAudioWorks.value.slice(0, 4)))
const playerStates = computed(() => audioPlayerStore.playerStates)
const hasAudioFragments = annotatedAudioWorks.value.some(({ audio }) => audio)

if (hasAudioFragments) {
  setupGtmCdpEvent('CV07 - Music fragment available')
}

const toggleShowAll = () => {
  showAll.value = !showAll.value
  if (showAll.value) {
    gtmCdpAction('CV07 - See all works clicked')
  }
}

const toggleAudio = (id: string) => {
  const isPlaying = computed(() => playerStates.value[id])
  if (!playerStates.value[id]) {
    gtmCdpAction('CV07 - Works music fragment clicked')
  }

  if (isPlaying.value) {
    gtmCdpAction('CV07 - Music fragment paused')
  }

  audioPlayerStore.toggleAudio(id)
}

const lastAudioItem = computed(() => {
  return (
    props.audioWorks
      .slice()
      .reverse()
      .find((item) => item.audio !== null) || {}
  )
})

// TODO: ADD WATCHER FOR GTM Events
onMounted(() => {
  watch(
    () => props.headerAudioIsPlaying,
    (newVal) => {
      if (newVal !== playerStates.value[lastAudioItem.value.id]) {
        audioPlayerStore.toggleAudio(lastAudioItem.value.id)
      }
    },
  )
})
</script>
